import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { Client } from '~/api/clients/types';
import type { SubmittedInvoice } from '~/api/invoices/types';

import { Button, Separator } from '~/components';
import { creativeShelter } from '~/constants/creativeShelter';
import { getVatRegime, isApproved } from '~/features/documents';
import { useIntl } from '~/hooks/useIntl';

import { PaymentStatusTag } from '../PaymentStatusTag';
import { breakdownActionStyle } from './InvoiceSplit.css';
import { InvoiceSplitCard } from './InvoiceSplitCard';

type Props = {
  client: Client;
  invoice: SubmittedInvoice;
  onPreview: () => void;
};

export const CoInvoiceCard = ({ client, invoice, onPreview }: Props) => {
  const { formatCurrency, formatDate } = useIntl();
  const { t } = useTranslation(['invoices', 'lines']);

  const { calculationData } = invoice;
  const vatRegime = getVatRegime(client);
  const hasPaymentDetails = isApproved(invoice);

  const showPartialPayments = invoice.status !== 'Credited' && invoice.coPaidDate === null && invoice.coPaidAmounts.length > 0;
  const paidAmount = showPartialPayments ? invoice.coPaidAmounts.reduce((sum, { amount }) => sum + amount, 0) : 0;

  return (
    <InvoiceSplitCard.Root>
      <InvoiceSplitCard.Overview>
        <InvoiceSplitCard.Header
          tag={
            hasPaymentDetails && (
              <PaymentStatusTag
                status={
                  invoice.status === 'Credited'
                    ? 'credited'
                    : invoice.coPaidDate !== null
                      ? 'coPaid'
                      : invoice.coPartiallyPaid
                        ? 'partiallyPaid'
                        : 'notPaid'
                }
              />
            )
          }
        >
          {invoice.coEntryNumber !== null
            ? `${t('invoices:details.split.co.eyebrow')} | ${invoice.coEntryNumber}`
            : t('invoices:details.split.co.eyebrow')}
        </InvoiceSplitCard.Header>

        <InvoiceSplitCard.Title from={creativeShelter.companyName} to={client.name} />

        <InvoiceSplitCard.Description>{t('invoices:details.split.co.description')}</InvoiceSplitCard.Description>

        <InvoiceSplitCard.Highlight label={t('invoices:details.split.co.highlight')}>
          {formatCurrency(calculationData.coInclVat)}
        </InvoiceSplitCard.Highlight>

        <InvoiceSplitCard.Rows>
          <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.dueDate')}>{formatDate(invoice.dueDate)}</InvoiceSplitCard.Row>

          {hasPaymentDetails && (
            <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.paidDate')}>
              {invoice.coPaidDate !== null ? formatDate(invoice.coPaidDate) : '–'}
            </InvoiceSplitCard.Row>
          )}

          {showPartialPayments && (
            <>
              {invoice.coPaidAmounts.map(({ amount, date }, index) => (
                <InvoiceSplitCard.Row
                  key={index}
                  label={
                    invoice.coPaidAmounts.length === 1
                      ? t('invoices:details.paymentInfo.alreadyPaid')
                      : t('invoices:details.paymentInfo.partialPayment', { number: index + 1 })
                  }
                >
                  {formatDate(date)}: {formatCurrency(amount)}
                </InvoiceSplitCard.Row>
              ))}

              {invoice.coPaidAmounts.length > 1 && (
                <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.alreadyPaid')}>
                  {formatCurrency(paidAmount)}
                </InvoiceSplitCard.Row>
              )}

              <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.remaining')}>
                {formatCurrency(calculationData.coInclVat - paidAmount)}
              </InvoiceSplitCard.Row>
            </>
          )}

          {invoice.coTransportType !== null && (
            <InvoiceSplitCard.Row label={t('invoices:details.paymentInfo.transportType.label')}>
              {t(`invoices:details.paymentInfo.transportType.options.${invoice.coTransportType === 'Peppol' ? 'Peppol' : 'Email'}`)}
            </InvoiceSplitCard.Row>
          )}
        </InvoiceSplitCard.Rows>
      </InvoiceSplitCard.Overview>

      <InvoiceSplitCard.Breakdown>
        <InvoiceSplitCard.AmountGroup>
          <InvoiceSplitCard.Amount label={t('invoices:details.split.amountExclVat')}>
            {formatCurrency(calculationData.coExclVat)}
          </InvoiceSplitCard.Amount>

          {vatRegime === 'applied' ? (
            calculationData.coVatAmounts
              .filter(({ amount }) => amount > 0)
              .map(({ amount, percentage }) => (
                <InvoiceSplitCard.Amount key={percentage} label={t('lines:vatPercentage', { percentage })}>
                  {formatCurrency(amount)}
                </InvoiceSplitCard.Amount>
              ))
          ) : (
            <InvoiceSplitCard.Amount label={t('lines:vat.header')}>
              {match(vatRegime)
                .with('shifted', () => t('lines:vatShifted'))
                .with('export', () => t('lines:vatExported'))
                .with('exceptionallyExempt', () => t('invoices:details.paymentInfo.vatExceptionallyExempt'))
                .exhaustive()}
            </InvoiceSplitCard.Amount>
          )}

          <InvoiceSplitCard.Amount isTotal label={t('invoices:details.split.totalInclVat')}>
            {formatCurrency(calculationData.coInclVat)}
          </InvoiceSplitCard.Amount>
        </InvoiceSplitCard.AmountGroup>

        <Separator />

        <Button className={breakdownActionStyle} emphasis="tertiary" icon="ArrowRight" iconPlacement="right" onClick={onPreview}>
          {t('invoices:details.split.co.preview')}
        </Button>
      </InvoiceSplitCard.Breakdown>
    </InvoiceSplitCard.Root>
  );
};
